import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { LearningMode, ProgrammingLanguage } from '@unity-learning/shared';
import { getUserSettings, updateUserSettings } from '../services/api';

interface LearningModeContextType {
  learningMode: LearningMode;
  programmingLanguage: ProgrammingLanguage;
  setLearningMode: (mode: LearningMode) => void;
  setProgrammingLanguage: (lang: ProgrammingLanguage) => void;
  syncWithUser: (userId: string) => void;
  isUnityMode: boolean;
  isPythonJsMode: boolean;
  isGodotMode: boolean;
}

const LearningModeContext = createContext<LearningModeContextType | undefined>(undefined);

export const LearningModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [learningMode, setLearningModeState] = useState<LearningMode>(() => {
    const saved = localStorage.getItem('learningMode') as LearningMode;
    return saved && Object.values(LearningMode).includes(saved) ? saved : LearningMode.UNITY_CSHARP;
  });

  const [programmingLanguage, setProgrammingLanguageState] = useState<ProgrammingLanguage>(() => {
    const saved = localStorage.getItem('programmingLanguage') as ProgrammingLanguage;
    return saved && Object.values(ProgrammingLanguage).includes(saved) ? saved : ProgrammingLanguage.CSHARP;
  });

  const userIdRef = useRef<string | null>(null);
  const syncedRef = useRef(false);

  const persistToBackend = useCallback((mode: LearningMode, lang: ProgrammingLanguage) => {
    const uid = userIdRef.current;
    if (!uid) return;
    // keepalive : un rechargement complet juste après le clic (F5, lien externe) annulait
    // la requête (ERR_ABORTED) et le mode choisi était perdu.
    updateUserSettings(uid, { learningMode: mode, programmingLanguage: lang }, { keepalive: true }).catch(() => {});
  }, []);

  const setLearningMode = (mode: LearningMode) => {
    setLearningModeState(mode);
    localStorage.setItem('learningMode', mode);
    let lang = programmingLanguage;
    if (mode === LearningMode.UNITY_CSHARP) {
      lang = ProgrammingLanguage.CSHARP;
      setProgrammingLanguageState(lang);
      localStorage.setItem('programmingLanguage', lang);
    } else if (mode === LearningMode.PYTHON_JS) {
      lang = ProgrammingLanguage.PYTHON;
      setProgrammingLanguageState(lang);
      localStorage.setItem('programmingLanguage', lang);
    } else if (mode === LearningMode.GODOT) {
      lang = ProgrammingLanguage.GDSCRIPT;
      setProgrammingLanguageState(lang);
      localStorage.setItem('programmingLanguage', lang);
    }
    persistToBackend(mode, lang);
  };

  const setProgrammingLanguage = (lang: ProgrammingLanguage) => {
    setProgrammingLanguageState(lang);
    localStorage.setItem('programmingLanguage', lang);
    persistToBackend(learningMode, lang);
  };

  const syncWithUser = useCallback((userId: string) => {
    userIdRef.current = userId;
    if (syncedRef.current) return;
    syncedRef.current = true;

    getUserSettings(userId)
      .then(settings => {
        if (!settings) return;
        const { learningMode: savedMode, programmingLanguage: savedLang } = settings;
        if (savedMode && Object.values(LearningMode).includes(savedMode)) {
          setLearningModeState(savedMode);
          localStorage.setItem('learningMode', savedMode);
        }
        if (savedLang && Object.values(ProgrammingLanguage).includes(savedLang)) {
          setProgrammingLanguageState(savedLang);
          localStorage.setItem('programmingLanguage', savedLang);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <LearningModeContext.Provider value={{
      learningMode,
      programmingLanguage,
      setLearningMode,
      setProgrammingLanguage,
      syncWithUser,
      isUnityMode: learningMode === LearningMode.UNITY_CSHARP,
      isPythonJsMode: learningMode === LearningMode.PYTHON_JS,
      isGodotMode: learningMode === LearningMode.GODOT
    }}>
      {children}
    </LearningModeContext.Provider>
  );
};

export const useLearningMode = () => {
  const context = useContext(LearningModeContext);
  if (context === undefined) {
    throw new Error('useLearningMode must be used within a LearningModeProvider');
  }
  return context;
};
